/**
 * useDevice — Game Day device & environment detection.
 *
 * Detects at call time (synchronous, no fetch required):
 *   isCapacitor    — running inside a Capacitor native app wrapper
 *   isPhone        — phone-sized touch device (not tablet)
 *   isTablet       — iPad or Android tablet (≥600px shortest side + touch)
 *   isIPad         — specifically iPadOS (includes iPadOS 13+ which reports as "Mac")
 *   isIOS          — any iOS/iPadOS device
 *   isAndroid      — Android device
 *   isStandalone   — added-to-home-screen PWA (not Capacitor, not browser chrome)
 *   browser        — 'safari' | 'chrome' | 'firefox' | 'edge' | 'other'
 *   layout         — 'capacitor' | 'tablet' | 'mobile' | 'desktop'
 *
 * Also provides:
 *   viewportHeight — live innerHeight (updates on resize / virtual keyboard)
 *   safeAreaTop    — CSS env() value for top notch/status bar
 *   safeAreaBottom — CSS env() value for home indicator
 */

import { useState, useEffect } from 'react';

// ─────────────────────────────────────────────────────────────────
// Static detection (runs once, no React, safe to call anywhere)
// ─────────────────────────────────────────────────────────────────
export function detectDevice() {
  const ua  = navigator.userAgent;
  const win = window;

  // ── Capacitor ─────────────────────────────────────────────────
  const isCapacitor =
    typeof win.Capacitor !== 'undefined' &&
    (win.Capacitor?.isNativePlatform?.() === true || win.Capacitor?.isNative === true);

  // ── Platform ──────────────────────────────────────────────────
  const isAndroid = /Android/i.test(ua);

  // iPadOS 13+ reports "Macintosh" in UA with maxTouchPoints > 1
  const isIPad =
    /iPad/i.test(ua) ||
    (navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua) && !/iPhone/i.test(ua));

  const isIPhone = /iPhone|iPod/i.test(ua);
  const isIOS    = isIPad || isIPhone;

  // ── Form factor ───────────────────────────────────────────────
  // Tablet: iPad, or Android with shortest screen side ≥ 600px
  const shortSide = Math.min(screen.width, screen.height);
  const isTablet  = isIPad || (isAndroid && shortSide >= 600 && 'ontouchstart' in win);
  const isPhone   = (isIOS || isAndroid) && !isTablet;

  // ── Standalone PWA ────────────────────────────────────────────
  const isStandalone =
    win.matchMedia?.('(display-mode: standalone)').matches === true ||
    win.navigator.standalone === true;

  // ── Browser ───────────────────────────────────────────────────
  // Order matters: Edge has "Chrome" in UA; Samsung has "Chrome"; Chrome has no "Edg"
  let browser = 'other';
  if (/Edg\//.test(ua))                               browser = 'edge';
  else if (/Firefox\//.test(ua))                      browser = 'firefox';
  else if (/Chrome\//.test(ua) && !/OPR/.test(ua))    browser = 'chrome';
  else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) browser = 'safari';
  else if (/OPR|Opera/.test(ua))                      browser = 'opera';

  // ── Layout decision ───────────────────────────────────────────
  // Priority: Capacitor > tablet > phone > desktop
  let layout = 'desktop';
  if (isCapacitor)      layout = 'capacitor';
  else if (isTablet)    layout = 'tablet';
  else if (isPhone)     layout = 'mobile';

  return {
    isCapacitor,
    isIOS, isIPad, isIPhone, isAndroid,
    isTablet, isPhone,
    isStandalone,
    browser,
    layout,
    // Convenience flags
    isMobileBrowser: (isPhone || isTablet) && !isCapacitor && !isStandalone,
    isNativeOrStandalone: isCapacitor || isStandalone,
  };
}

// Memoize — detection result never changes within a page session
let _cached = null;
export function getDevice() {
  if (!_cached) _cached = detectDevice();
  return _cached;
}

// ─────────────────────────────────────────────────────────────────
// React hook — adds reactive viewport height for keyboard handling
// ─────────────────────────────────────────────────────────────────
export function useDevice() {
  const device = getDevice();
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    if (!device.isPhone && !device.isTablet) return;

    // Set CSS custom property for viewport height (handles Chrome/Safari URL bar)
    const update = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      setViewportHeight(window.innerHeight);
    };

    update();

    // Use ResizeObserver on document.body for virtual keyboard events (more reliable than resize)
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(document.body);
    } else {
      window.addEventListener('resize', update, { passive: true });
    }

    // Also listen to orientationchange
    window.addEventListener('orientationchange', update, { passive: true });

    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return { ...device, viewportHeight };
}

// ─────────────────────────────────────────────────────────────────
// Add mobile performance class to <html> immediately (no flash)
// Called once from main.jsx
// ─────────────────────────────────────────────────────────────────
export function applyDeviceClasses() {
  const { layout, isIOS, isAndroid, browser, isCapacitor } = getDevice();
  const el = document.documentElement;

  el.classList.add(`device-${layout}`);                         // device-mobile / device-tablet / device-capacitor / device-desktop
  if (isIOS)       el.classList.add('os-ios');
  if (isAndroid)   el.classList.add('os-android');
  if (isCapacitor) el.classList.add('env-capacitor');
  el.classList.add(`browser-${browser}`);                       // browser-safari / browser-chrome / …
}
